/* eslint-disable no-control-regex */
import z from "zod";

import { ARABIC_TEXT_WITH_NUMBERS_REGEX } from "@/utils/constants/arabicRegex";

export const MAX_FILE_SIZE = 20 * 1024 * 1024;

export const ALLOWED_TYPES = ["application/pdf", "image/jpeg", "image/png"];

const nutritionField = z
  .string()
  .regex(/^\d+(\.\d)?$/, "Must be a number ≥ 0 with up to 1 decimal")
  .trim()
  .refine(v => Number(v) <= 1_000_000, {
    message: "Maximum value is 1,000,000",
  })
  .optional()
  .or(z.literal(""));

export const TastesSchema = z.object({
  Spice: z.number().min(0).max(5),
  Sweetness: z.number().min(0).max(5),
  Sourness: z.number().min(0).max(5),
  Bitterness: z.number().min(0).max(5),
  Umami: z.number().min(0).max(5),
});

export type TastesType = z.infer<typeof TastesSchema>;

export const DishImageVariantSchema = z.object({
  url: z.string(),
  type: z.string(),
});

export const DishImageMediaSchema = z.object({
  id: z.string(),
  type: z.string(),
  variants: z.array(DishImageVariantSchema),
  base64: z.string().optional(),
  mediaId: z.string().optional(),
  order: z.number().optional(),
});

export type DishImageMedia = z.infer<typeof DishImageMediaSchema>;

export const getDishMediaPreviewSrc = (media?: DishImageMedia): string | null =>
  media?.base64 ?? media?.variants[0]?.url ?? null;

export const isDishMediaVideo = (media?: DishImageMedia): boolean => {
  if (!media) return false;
  if (media.type.startsWith("video")) return true;

  const previewSrc = getDishMediaPreviewSrc(media);
  return !!previewSrc && previewSrc.toLowerCase().endsWith(".mp4");
};

export const sortDishImagesByOrder = (
  media: DishImageMedia[] = []
): DishImageMedia[] =>
  [...media].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

/**
 * Decimals to allow when we don't know the menu's currency yet. Most currencies
 * use 2, so it is the safe assumption while the menu is still loading.
 */
export const DEFAULT_PRICE_DECIMALS = 2;

/**
 * Price validation for a given currency. The number of decimals is not fixed:
 * SAR uses 2, while JOD and KWD use 3, so a Jordanian price of 2.750 has to be
 * accepted where a Saudi one of 2.75 does not gain a third digit.
 */
export const priceSchema = (decimals: number = DEFAULT_PRICE_DECIMALS) =>
  z
    .string()
    .trim()
    .min(1, "Price is required")
    .regex(
      new RegExp(`^\\d+(\\.\\d{1,${decimals}})?$`),
      `Must be a valid price (max ${decimals} decimal places)`
    )
    .refine(v => parseFloat(v) > 0, {
      message: "Price must be greater than 0",
    })
    .refine(v => Number(v) <= 1_000_000, {
      message: "Maximum value is 1,000,000",
    });

export const DishFormSchema = z.object({
  dishNameEnglish: z
    .string()
    .trim()
    .min(1, "Dish name (English) is required")
    .max(100, "Max 100 characters")
    .refine(value => /^[\u0000-\u007F]+$/.test(value), {
      message: "Only English letters, numbers and symbols are allowed.",
    })
    .trim(),

  dishNameArabic: z
    .string()
    .trim()
    .min(1, "Dish name (Arabic) is required")
    .max(100, "Max 100 characters")
    .regex(ARABIC_TEXT_WITH_NUMBERS_REGEX, {
      message: "Only Arabic characters and numbers are allowed.",
    })
    .trim(),

  descriptionEnglish: z.string().trim().max(500).optional().or(z.literal("")),
  descriptionArabic: z
    .string()
    .trim()
    .max(500)
    .regex(ARABIC_TEXT_WITH_NUMBERS_REGEX, {
      message: "Description must contain only Arabic characters and numbers.",
    })
    .trim()
    .optional()
    .or(z.literal("")),

  preparationTime: z
    .string()
    .regex(/^\d+$/, "Must be a positive integer")
    .trim()
    .refine(v => Number(v) <= 1_000_000, {
      message: "Maximum value is 1,000,000",
    })
    .optional()
    .or(z.literal("")),

  price: priceSchema(DEFAULT_PRICE_DECIMALS),

  active: z.boolean().optional(),
  unavailableBranchIds: z.array(z.string()).optional(),

  flagsAndStatuses: z.array(z.string()).optional(),

  // Step 2 — meal & taste
  meal: z.array(z.string()).optional(),
  occasion: z.array(z.string()).optional(),
  ratings: TastesSchema.optional(),

  // Step 3 — diet & nutrition
  dietTags: z.array(z.string()).optional(),
  allergenTags: z.array(z.string()).optional(),
  calories: nutritionField,
  burnCaloriesTime: nutritionField,
  energy: nutritionField,
  protein: nutritionField,
  fiber: nutritionField,
  totalFat: nutritionField,
  totalSugar: nutritionField,
  saturatedFat: nutritionField,
  addedSugars: nutritionField,
  transFat: nutritionField,
  salt: nutritionField,
  cholesterol: nutritionField,
  sodium: nutritionField,
  carbohydrate: nutritionField,
  caffeine: nutritionField,

  saltContent: z.boolean().optional(),

  id: z.string().optional(),
  mediaItems: z
    .array(
      z.object({
        mediaId: z.string().optional(),
        processedMediaId: z.string().optional(),
      })
    )
    .optional(),
  images: z
    .object({
      media: z.array(DishImageMediaSchema),
    })
    .optional(),
});

/**
 * The dish schema with price validation matched to a currency. A screen that
 * knows the menu's currency should use this rather than `DishFormSchema`, so a
 * Jordanian menu accepts 2.750 while a Saudi one still stops at 2 decimals.
 */
export const makeDishFormSchema = (decimals: number = DEFAULT_PRICE_DECIMALS) =>
  DishFormSchema.extend({ price: priceSchema(decimals) });

export type DishFormType = z.infer<typeof DishFormSchema>;

/**
 * A category and the dishes under it, with prices matched to a currency.
 *
 * The dishes have to be built with `makeDishFormSchema` rather than the plain
 * `DishFormSchema`, or every price in the menu is checked against 2 decimals
 * no matter what the brand trades in — which rejects a Jordanian 2.750 that
 * the dish form itself accepted.
 */
export const makeCategorySchema = (decimals: number = DEFAULT_PRICE_DECIMALS) =>
  z.object({
    id: z.string().optional(),
    categoryDictId: z.string().optional(),
    order: z.number().optional(),
    categoryNameEn: z
      .string()
      .min(1, "Category name (English) is required")
      .max(60, "Max 60 characters"),
    categoryNameAr: z.string().optional(),
    dishes: z.array(makeDishFormSchema(decimals)),
    isDuplicate: z.boolean().optional(),
  });

/** The 2-decimal shape, kept for the types inferred from it. */
export const categorySchema = makeCategorySchema();

const menuUploadSourceRefine = (
  data: {
    file?: File;
    url?: string;
  },
  ctx: z.RefinementCtx
) => {
  const hasFile = !!data.file;
  const hasUrl = !!data.url?.trim();

  if (!hasFile && !hasUrl) {
    return;
  }

  if (hasFile && hasUrl) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["file"],
      message: "Only one source can be selected.",
    });

    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ["url"],
      message: "Only one source can be selected.",
    });

    return;
  }

  if (data.file) {
    if (!ALLOWED_TYPES.includes(data.file.type)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file"],
        message: "Only PDF, JPG and PNG files are supported.",
      });
    }

    if (data.file.size > MAX_FILE_SIZE) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["file"],
        message: "File size must not exceed 20 MB.",
      });
    }
  }

  if (hasUrl) {
    try {
      const parsed = new URL(data.url!.trim());

      if (!["http:", "https:"].includes(parsed.protocol)) {
        throw new Error();
      }
    } catch {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["url"],
        message: "Please enter a valid URL.",
      });
    }
  }
};

const menuFormBaseSchema = z.object({
  cuisine: z
    .array(z.string().min(1, { message: "Please select a cuisine." }))
    .min(1, { message: "At least one cuisine is required." }),
  file: z.instanceof(File).optional(),
  url: z.string().optional(),
  menuId: z.string().optional(),
  isActive: z.boolean().optional(),
  sourceFileName: z.string().optional(),
  sourceFileUrl: z.string().optional(),
  sourceMenuUrl: z.string().optional(),
  updatedAt: z.string().optional(),
});

/** Wizard step: cuisine + upload source only; dishes validated in drawer */
export const WizardMenuFormSchema = menuFormBaseSchema
  .extend({
    categories: z.array(z.unknown()).optional(),
  })
  .superRefine(menuUploadSourceRefine);

/**
 * The whole menu form, with dish prices matched to the brand's currency.
 *
 * A screen that knows the currency should use this rather than `FormSchema`,
 * so saving a Jordanian menu does not fail on the 2.750 prices already in it.
 */
export const makeFormSchema = (decimals: number = DEFAULT_PRICE_DECIMALS) =>
  menuFormBaseSchema
    .extend({
      categories: z.array(makeCategorySchema(decimals)).optional(),
    })
    .superRefine(menuUploadSourceRefine);

/** The 2-decimal shape, kept for the types inferred from it. */
export const FormSchema = makeFormSchema();

export type BrandFormMenuType = z.infer<typeof FormSchema>;
export type CategoryType = z.infer<typeof categorySchema>;

export const NUTRITION_FIELDS_REQUIRED: (keyof DishFormType)[] = [
  "calories",
  "burnCaloriesTime",
  "energy",
  "protein",
  "fiber",
  "totalFat",
  "totalSugar",
  "saturatedFat",
  "addedSugars",
  "transFat",
  "salt",
  "cholesterol",
  "sodium",
  "carbohydrate",
  "caffeine",
];

export const DETAILS_FIELDS_REQUIRED: (keyof DishFormType)[] = [
  "dishNameEnglish",
  "dishNameArabic",
  "descriptionEnglish",
  "descriptionArabic",
  "price",
  "preparationTime",
];
